import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  IconButton,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Input,
  Autocomplete,
  AutocompleteOption,
  Select,
  Option,
  Textarea,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/joy';
import { Plus as Add, Trash as Delete } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useJobFinancialSummary, useJobCosts, useAddJobCost, useDeleteJobCost, useJobCredits, useAddJobCredit, useDeleteJobCredit } from '../../hooks/useFinancials';
import { useJobPayments } from '../../hooks/useBilling';
import { useCommissions } from '../../hooks/useCommissions';
import { ResponsiveTable } from '../common/ResponsiveTable';
import { apiClient } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { CommissionBreakdown } from '../commissions/CommissionBreakdown';
import { CommissionSettings } from '../commissions/CommissionSettings';
import type { JobCost, JobCredit } from '../../types/financials';

interface JobFinancialsTabProps {
  jobId: string;
  showSummary?: boolean;
}

/**
 * Costs and credits can now point at the Distributor or Subcontractor record
 * they belong to, which is what per-vendor spend rolls up on. `vendor` stays
 * as the typed-in name for one-off suppliers, and holds the display name of
 * whichever record is linked.
 */
type VendorLink = {
  distributor?: number | null;
  subcontractor?: number | null;
};

interface VendorOption {
  id: number;
  label: string;
  kind: 'distributor' | 'subcontractor';
}

const vendorSelection = (value: string | VendorOption | null): VendorLink & { vendor: string } => {
  if (!value) return { vendor: '', distributor: null, subcontractor: null };
  // A hand-typed name is a one-off supplier - no record to link to.
  if (typeof value === 'string') return { vendor: value, distributor: null, subcontractor: null };
  return {
    vendor: value.label,
    distributor: value.kind === 'distributor' ? value.id : null,
    subcontractor: value.kind === 'subcontractor' ? value.id : null,
  };
};

export const JobFinancialsTab: React.FC<JobFinancialsTabProps> = ({ jobId, showSummary = true }) => {
  const { user } = useAuth();
  const [editCommission, setEditCommission] = useState(false);
  const [showAddCostModal, setShowAddCostModal] = useState(false);
  const [showAddCreditModal, setShowAddCreditModal] = useState(false);
  const [newCost, setNewCost] = useState<Partial<JobCost> & VendorLink>({
    cost_type: 'materials',
    description: '',
    amount: '',
    vendor: '',
    notes: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [newCredit, setNewCredit] = useState<Partial<JobCredit> & VendorLink>({
    credit_type: 'material_return',
    description: '',
    amount: '',
    vendor: '',
    notes: '',
    date: new Date().toISOString().split('T')[0],
  });

  const summaryQuery = useJobFinancialSummary(jobId);
  const costsQuery = useJobCosts(jobId);
  const creditsQuery = useJobCredits(jobId);
  // This tab only needs payments; avoid fetching estimates and invoices too.
  const paymentsQuery = useJobPayments(jobId);
  const financialSummary = summaryQuery.data;
  const costs = costsQuery.data ?? [];
  const credits = creditsQuery.data ?? [];
  const payments = paymentsQuery.data ?? [];
  const sections = [
    { label: 'Financial summary & commission estimate', query: summaryQuery },
    { label: 'Costs', query: costsQuery },
    { label: 'Credits', query: creditsQuery },
    { label: 'Payments', query: paymentsQuery },
  ];
  const transactionsIncomplete = [costsQuery, creditsQuery, paymentsQuery].some(
    query => query.data === undefined || !!query.error,
  );
  const addCostMutation = useAddJobCost();
  const deleteCostMutation = useDeleteJobCost();
  const addCreditMutation = useAddJobCredit();
  const deleteCreditMutation = useDeleteJobCredit();

  // Vendor pickers. Only fetched once a modal is open - the tab itself has no
  // use for them.
  const vendorPickerOpen = showAddCostModal || showAddCreditModal;

  const { data: distributorOptions = [] } = useQuery({
    queryKey: ['vendorOptions', 'distributors'],
    queryFn: async (): Promise<VendorOption[]> => {
      // The distributors app is mounted under /api/distributors/ and registers
      // its own `distributors` route, hence the doubled segment (see
      // lib/distributorsApi.ts). getAllPages because a picker that stops at
      // the 20-row default page silently hides vendors.
      const response = await apiClient.getAllPages<any>('/api/distributors/distributors/?is_active=true');
      return response.data.map((row) => ({ id: row.id, label: row.name, kind: 'distributor' as const }));
    },
    enabled: vendorPickerOpen,
    staleTime: 5 * 60 * 1000,
  });

  const { data: subcontractorOptions = [] } = useQuery({
    queryKey: ['vendorOptions', 'subcontractors'],
    queryFn: async (): Promise<VendorOption[]> => {
      const response = await apiClient.getAllPages<any>('/api/subcontractors/?status=active');
      return response.data.map((row) => ({ id: row.id, label: row.company_name, kind: 'subcontractor' as const }));
    },
    enabled: vendorPickerOpen,
    staleTime: 5 * 60 * 1000,
  });

  // Subcontractor costs are owed to a sub; everything else is bought from a
  // distributor. A credit can come back from either.
  const costVendorOptions = newCost.cost_type === 'subcontractor' ? subcontractorOptions : distributorOptions;
  const creditVendorOptions = useMemo(
    () => [...distributorOptions, ...subcontractorOptions],
    [distributorOptions, subcontractorOptions],
  );

  // The Autocompletes are value-controlled from the draft's FK. Left
  // uncontrolled they keep their own idea of what is selected, and re-picking
  // the option you had just typed over is a no-op - the link never gets made.
  const linkedOption = (options: VendorOption[], link: VendorLink) =>
    options.find((option) =>
      option.kind === 'distributor' ? option.id === link.distributor : option.id === link.subcontractor,
    ) || null;

  const handleAddCost = async () => {
    if (!newCost.description || !newCost.amount) return;
    
    try {
      await addCostMutation.mutateAsync({ jobId, costData: newCost });
      setShowAddCostModal(false);
      setNewCost({
        cost_type: 'materials',
        description: '',
        amount: '',
        vendor: '',
        distributor: null,
        subcontractor: null,
        notes: '',
        date: new Date().toISOString().split('T')[0],
      });
    } catch (error) {
      // Handle error silently
    }
  };

  const handleAddCredit = async () => {
    if (!newCredit.description || !newCredit.amount) return;
    
    try {
      await addCreditMutation.mutateAsync({ jobId, creditData: newCredit });
      setShowAddCreditModal(false);
      setNewCredit({
        credit_type: 'material_return',
        description: '',
        amount: '',
        vendor: '',
        distributor: null,
        subcontractor: null,
        notes: '',
        date: new Date().toISOString().split('T')[0],
      });
    } catch (error) {
      // Handle error silently
    }
  };

  const handleDeleteCost = async (costId: number) => {
    if (window.confirm('Are you sure you want to delete this cost?')) {
      try {
        await deleteCostMutation.mutateAsync({ costId, jobId });
      } catch (error) {
        // Handle error silently
      }
    }
  };

  const handleDeleteCredit = async (creditId: number) => {
    if (window.confirm('Are you sure you want to delete this credit?')) {
      try {
        await deleteCreditMutation.mutateAsync({ creditId, jobId });
      } catch (error) {
        // Handle error silently
      }
    }
  };

  const formatCurrency = (amount: string | number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Number(amount));
  };

  const formatPercentage = (value: string | number) => {
    return `${Number(value).toFixed(1)}%`;
  };

  // Combine costs, credits, and payments into transactions
  const transactions = [
    ...payments.map(payment => ({
      id: `payment-${payment.id}`,
      date: payment.payment_date,
      description: `${payment.payment_method} payment (${payment.status})${Number(payment.refunded_amount) > 0 ? ' · net of refunds' : ''}`,
      type: 'payment' as const,
      amount: payment.status === 'completed' ? String(payment.net_amount) : '0',
    })),
    ...costs.map(cost => ({
      id: `cost-${cost.id}`,
      date: cost.date,
      description: cost.description,
      type: 'cost' as const,
      amount: cost.amount,
      vendor: cost.vendor,
      cost_type: cost.cost_type,
      costId: cost.id,
    })),
    ...credits.map(credit => ({
      id: `credit-${credit.id}`,
      date: credit.date,
      description: credit.description,
      type: 'credit' as const,
      amount: credit.amount,
      vendor: credit.vendor,
      credit_type: credit.credit_type,
      creditId: credit.id,
    })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <Box>
      {sections.map(({ label, query }) => (
        <FinancialSectionStatus key={label} label={label} query={query} />
      ))}
      {/* Key Financial Metrics */}
      {showSummary && financialSummary && <><Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>Contract Total</Typography>
              <Typography level="h3" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(financialSummary.job_value)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>Total Costs</Typography>
              <Typography level="h3" sx={{ fontWeight: 'bold', color: 'danger.500' }}>
                {formatCurrency(financialSummary.total_costs)}
              </Typography>
              {financialSummary.total_credits && Number(financialSummary.total_credits) > 0 && (
                <Typography level="body-xs" sx={{ color: 'success.500' }}>
                  Credits: {formatCurrency(financialSummary.total_credits)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>Profit</Typography>
              <Typography level="h3" sx={{ fontWeight: 'bold', color: Number(financialSummary.profit) >= 0 ? 'success.500' : 'danger.500' }}>
                {formatCurrency(financialSummary.profit)}
              </Typography>
              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                {formatPercentage(financialSummary.profit_margin)} margin
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ mb: 1 }}>Outstanding</Typography>
              <Typography level="h3" sx={{ fontWeight: 'bold', color: Number(financialSummary.outstanding) > 0 ? 'warning.500' : 'success.500' }}>
                {formatCurrency(financialSummary.outstanding)}
              </Typography>
              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                {formatCurrency(financialSummary.total_payments)} paid
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      </>}

      {financialSummary?.commission_calculation && <Card sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Box><Typography level="h4">Commission calculation</Typography><Typography level="body-sm">{financialSummary.commission_calculation.name} · {financialSummary.commission_calculation.source === 'job' ? 'Job override' : 'Organization default'} · {financialSummary.assigned_to_name || 'No sales rep'}</Typography></Box>
          {(user?.is_organization_admin || user?.is_superuser) && <Button variant="outlined" onClick={() => setEditCommission(true)}>Customize for this job</Button>}
        </Stack>
        <CommissionBreakdown calculation={financialSummary.commission_calculation} />
        <Typography level="body-xs">Live estimate based on recorded job economics. Recorded commissions below retain their original calculation and payment status.</Typography>
      </Card>}
      <Modal open={editCommission} onClose={() => setEditCommission(false)}><ModalDialog sx={{ width: 'min(1200px, 96vw)', maxHeight: '94vh', overflow: 'auto' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center"><DialogTitle>Job commission override</DialogTitle><Button variant="plain" color="neutral" onClick={() => setEditCommission(false)}>Close</Button></Stack>
        <CommissionSettings jobId={jobId} onSaved={() => setEditCommission(false)} />
      </ModalDialog></Modal>

      {/* Sales Commission Tracking */}
      <SalesCommissionCard jobId={jobId} />

      {[addCostMutation.error, addCreditMutation.error, deleteCostMutation.error, deleteCreditMutation.error].filter(Boolean).map((error, i) => <Alert key={i} color="danger">{error instanceof Error ? error.message : String(error)}</Alert>)}
      {/* Recent Transactions */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Typography level="h4">Transactions</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="sm"
                startDecorator={<Add />}
                onClick={() => setShowAddCostModal(true)}
              >
                Add Cost
              </Button>
              <Button
                size="sm"
                startDecorator={<Add />}
                onClick={() => setShowAddCreditModal(true)}
                color="success"
              >
                Add Credit
              </Button>
            </Box>
          </Box>
          
          {transactionsIncomplete && <Alert color="warning" sx={{ mb: 2 }}>
            Transaction list is incomplete. Unavailable amounts are not counted as zero.
          </Alert>}
          {transactions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="neutral">{transactionsIncomplete ? 'Transactions are not fully available yet.' : 'No transactions recorded'}</Typography>
            </Box>
          ) : (
            <ResponsiveTable rows={transactions} getRowKey={t => t.id} pagination={{ pageSize: 20, itemNoun: 'transactions' }} columns={[
              { key: 'description', header: 'Description', mobileVariant: 'header', render: t => <Box>{t.description}{'vendor' in t && t.vendor && <Typography level="body-xs">{t.vendor}</Typography>}</Box> },
              { key: 'date', header: 'Date', render: t => new Date(t.date.length === 10 ? `${t.date}T12:00:00` : t.date).toLocaleDateString() },
              { key: 'type', header: 'Type', render: t => <Chip size="sm">{t.type === 'payment' ? 'Payment' : t.type === 'cost' ? t.cost_type : t.credit_type}</Chip> },
              { key: 'amount', header: 'Amount', render: t => <Typography color={t.type === 'cost' ? 'danger' : 'success'}>{t.type === 'cost' ? '−' : '+'}{formatCurrency(t.amount)}</Typography> },
            ]} rowActions={t => t.type === 'payment' ? null : <IconButton size="sm" color="danger" aria-label={`Delete ${t.description}`} onClick={() => t.type === 'cost' ? handleDeleteCost(t.costId) : handleDeleteCredit(t.creditId)} disabled={deleteCostMutation.isPending || deleteCreditMutation.isPending}><Delete /></IconButton>} />
          )}
        </CardContent>
      </Card>

      {/* Add Cost Modal */}
      <Modal open={showAddCostModal} onClose={() => setShowAddCostModal(false)}>
        <ModalDialog size="md">
          <DialogTitle>Add Job Cost</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl>
                <FormLabel>Cost Type</FormLabel>
                <Select
                  value={newCost.cost_type}
                  onChange={(_, value) => {
                    const costType = value as JobCost['cost_type'];
                    // Only a flip in or out of 'subcontractor' swaps the
                    // picker's source, and only then is an existing link
                    // pointing at the wrong list. Materials -> Equipment must
                    // keep the distributor the user already chose.
                    const sourceChanged = (costType === 'subcontractor') !== (newCost.cost_type === 'subcontractor');
                    const linked = newCost.distributor || newCost.subcontractor;
                    setNewCost({
                      ...newCost,
                      cost_type: costType,
                      ...(sourceChanged && linked ? vendorSelection(null) : {}),
                    });
                  }}
                >
                  <Option value="materials">Materials</Option>
                  <Option value="labor">Labor</Option>
                  <Option value="equipment">Equipment</Option>
                  <Option value="subcontractor">Subcontractor</Option>
                  <Option value="permits">Permits</Option>
                  <Option value="insurance">Insurance</Option>
                  <Option value="other">Other</Option>
                </Select>
              </FormControl>
              
              <FormControl required>
                <FormLabel>Description</FormLabel>
                <Input
                  value={newCost.description}
                  onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
                />
              </FormControl>
              
              <Grid container spacing={2}>
                <Grid xs={6}>
                  <FormControl required>
                    <FormLabel>Amount</FormLabel>
                    <Input
                      type="number"
                      value={newCost.amount}
                      onChange={(e) => setNewCost({ ...newCost, amount: e.target.value })}
                      startDecorator="$"
                      slotProps={{
                        input: {
                          min: 0,
                          step: 0.01,
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={6}>
                  <FormControl>
                    <FormLabel>Date</FormLabel>
                    <Input
                      type="date"
                      value={newCost.date}
                      onChange={(e) => setNewCost({ ...newCost, date: e.target.value })}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              
              <FormControl>
                <FormLabel>Vendor</FormLabel>
                <Autocomplete
                  freeSolo
                  placeholder={newCost.cost_type === 'subcontractor' ? 'Search subcontractors' : 'Search distributors'}
                  options={costVendorOptions}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                  value={linkedOption(costVendorOptions, newCost)}
                  inputValue={newCost.vendor || ''}
                  onInputChange={(_, value, reason) => {
                    // Only free typing clears the link; 'reset' fires as part
                    // of picking an option, which onChange already handled.
                    if (reason === 'input') {
                      setNewCost({ ...newCost, vendor: value, distributor: null, subcontractor: null });
                    }
                  }}
                  onChange={(_, value) => setNewCost({ ...newCost, ...vendorSelection(value) })}
                  renderOption={(props, option) => (
                    <AutocompleteOption {...props} key={`${option.kind}-${option.id}`}>
                      {option.label}
                    </AutocompleteOption>
                  )}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Invoice Number</FormLabel>
                <Input
                  value={newCost.invoice_number}
                  onChange={(e) => setNewCost({ ...newCost, invoice_number: e.target.value })}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  minRows={2}
                  value={newCost.notes}
                  onChange={(e) => setNewCost({ ...newCost, notes: e.target.value })}
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={() => setShowAddCostModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddCost}
              disabled={!newCost.description || !newCost.amount || addCostMutation.isPending}
              loading={addCostMutation.isPending}
            >
              Add Cost
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Add Credit Modal */}
      <Modal open={showAddCreditModal} onClose={() => setShowAddCreditModal(false)}>
        <ModalDialog size="md">
          <DialogTitle>Add Job Credit</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl>
                <FormLabel>Credit Type</FormLabel>
                <Select
                  value={newCredit.credit_type}
                  onChange={(_, value) => setNewCredit({ ...newCredit, credit_type: value as JobCredit['credit_type'] })}
                >
                  <Option value="material_return">Material Return</Option>
                  <Option value="overpayment_refund">Overpayment Refund</Option>
                  <Option value="vendor_credit">Vendor Credit</Option>
                  <Option value="discount">Discount</Option>
                  <Option value="other">Other</Option>
                </Select>
              </FormControl>
              
              <FormControl required>
                <FormLabel>Description</FormLabel>
                <Input
                  value={newCredit.description}
                  onChange={(e) => setNewCredit({ ...newCredit, description: e.target.value })}
                />
              </FormControl>
              
              <Grid container spacing={2}>
                <Grid xs={6}>
                  <FormControl required>
                    <FormLabel>Amount</FormLabel>
                    <Input
                      type="number"
                      value={newCredit.amount}
                      onChange={(e) => setNewCredit({ ...newCredit, amount: e.target.value })}
                      startDecorator="$"
                      slotProps={{
                        input: {
                          min: 0,
                          step: 0.01,
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={6}>
                  <FormControl>
                    <FormLabel>Date</FormLabel>
                    <Input
                      type="date"
                      value={newCredit.date}
                      onChange={(e) => setNewCredit({ ...newCredit, date: e.target.value })}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              
              <FormControl>
                <FormLabel>Vendor</FormLabel>
                <Autocomplete
                  freeSolo
                  placeholder="Search distributors and subcontractors"
                  options={creditVendorOptions}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                  value={linkedOption(creditVendorOptions, newCredit)}
                  inputValue={newCredit.vendor || ''}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                      setNewCredit({ ...newCredit, vendor: value, distributor: null, subcontractor: null });
                    }
                  }}
                  onChange={(_, value) => setNewCredit({ ...newCredit, ...vendorSelection(value) })}
                  renderOption={(props, option) => (
                    <AutocompleteOption {...props} key={`${option.kind}-${option.id}`}>
                      {option.label}
                    </AutocompleteOption>
                  )}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Reference Number</FormLabel>
                <Input
                  value={newCredit.reference_number}
                  onChange={(e) => setNewCredit({ ...newCredit, reference_number: e.target.value })}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  minRows={2}
                  value={newCredit.notes}
                  onChange={(e) => setNewCredit({ ...newCredit, notes: e.target.value })}
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={() => setShowAddCreditModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddCredit}
              disabled={!newCredit.description || !newCredit.amount || addCreditMutation.isPending}
              loading={addCreditMutation.isPending}
              color="success"
            >
              Add Credit
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

function FinancialSectionStatus({ label, query }: {
  label: string;
  query: { data: unknown; error: unknown; isFetching: boolean; refetch: () => unknown };
}) {
  if (query.error) return <Alert color="warning" sx={{ mb: 2 }}
    endDecorator={<Button size="sm" variant="outlined" loading={query.isFetching}
      onClick={() => void query.refetch()} aria-label={`Retry ${label}`}>Retry</Button>}>
    <Box><Typography level="title-sm">{label} {query.data === undefined ? 'unavailable' : 'could not be refreshed'}</Typography>
      <Typography level="body-sm">{query.data === undefined
        ? 'This section could not load. Other available sections are shown below.'
        : 'Showing previously loaded data. It may be out of date.'}</Typography></Box>
  </Alert>;
  if (query.data === undefined) return <Alert color="neutral" sx={{ mb: 2 }} startDecorator={<CircularProgress size="sm" />}>
    Loading {label.toLowerCase()}…
  </Alert>;
  return null;
}

// Sales Commission tracking sub-component
function SalesCommissionCard({ jobId }: { jobId: string }) {
  // Fetch commissions for this specific job via the job filter
  const commissionQuery = useCommissions({});
  const { data } = commissionQuery;
  // Filter client-side for this job (the API also supports job filter but we use what we have)
  const jobCommissions = data?.results?.filter((c) => String(c.job) === jobId) ?? [];

  if (jobCommissions.length === 0) return <FinancialSectionStatus label="Recorded commissions" query={commissionQuery} />;

  const c = jobCommissions[0];
  const statusColors: Record<string, 'primary' | 'warning' | 'success' | 'neutral'> = {
    accrued: 'primary',
    on_hold: 'warning',
    paid: 'success',
    cancelled: 'neutral',
  };

  const formatCurr = (val: string | number) => {
    const n = typeof val === 'string' ? parseFloat(val) : val;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
  };

  const formatDt = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <FinancialSectionStatus label="Recorded commissions" query={commissionQuery} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography level="h4">Sales Commission</Typography>
          <Chip size="sm" color={statusColors[c.status] || 'neutral'} variant="soft">
            {c.status === 'on_hold' ? 'On Hold' : c.status.charAt(0).toUpperCase() + c.status.slice(1)}
          </Chip>
        </Box>
        <Grid container spacing={2}>
          <Grid xs={12} sm={3}>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>Sales Rep</Typography>
            <Typography level="body-lg">{c.sales_rep_name}</Typography>
          </Grid>
          <Grid xs={12} sm={3}>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>Commission</Typography>
            <Typography level="h4" sx={{ fontWeight: 'bold', color: 'var(--brand-text, #7013f5)' }}>
              {formatCurr(c.final_amount)}
            </Typography>
          </Grid>
          <Grid xs={12} sm={3}>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>Accrued</Typography>
            <Typography level="body-lg">{formatDt(c.accrued_at)}</Typography>
          </Grid>
          <Grid xs={12} sm={3}>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>Paid</Typography>
            <Typography level="body-lg">{c.paid_at ? formatDt(c.paid_at) : 'Not yet'}</Typography>
          </Grid>
        </Grid>
        {c.override_amount && (
          <Alert color="warning" size="sm" sx={{ mt: 2 }}>
            Override: {formatCurr(c.override_amount)} — {c.override_reason}
          </Alert>
        )}
        {c.status === 'on_hold' && c.hold_reason && (
          <Alert color="warning" size="sm" sx={{ mt: 2 }}>
            On hold: {c.hold_reason}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
